import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  AlertTriangle, Plus, Trash2, Edit, Eye, Car, Wrench, ShieldOff,
  AlertCircle, Flame, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { IncidentForm } from '../components/IncidentForm';

const INCIDENT_TYPES = [
  { value: 'accident',   label: 'Accident',    icon: Car },
  { value: 'breakdown',  label: 'Breakdown',   icon: Wrench },
  { value: 'theft',      label: 'Theft',       icon: ShieldOff },
  { value: 'vandalism',  label: 'Vandalism',   icon: Flame },
  { value: 'near_miss',  label: 'Near Miss',   icon: AlertCircle },
  { value: 'other',      label: 'Other',       icon: AlertTriangle },
];

const STATUS_STYLES = {
  open:        { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Open' },
  in_progress: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'In Progress' },
  closed:      { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Closed' },
};

const TYPE_STYLES = {
  accident:   'bg-red-100 text-red-700',
  breakdown:  'bg-orange-100 text-orange-700',
  theft:      'bg-purple-100 text-purple-700',
  vandalism:  'bg-pink-100 text-pink-700',
  near_miss:  'bg-amber-100 text-amber-700',
  other:      'bg-slate-100 text-slate-600',
};

const emptyForm = () => ({
  vehicle_id: '',
  date: new Date().toISOString().split('T')[0],
  incident_type: 'accident',
  description: '',
  location: '',
  driver_id: '',
  third_party_name: '',
  third_party_contact: '',
  third_party_vehicle_reg: '',
  police_case_number: '',
  damage_description: '',
  damage_estimate: '',
  status: 'open',
  resolution_notes: '',
});

export default function IncidentsPage() {
  const [incidents, setIncidents]         = useState([]);
  const [vehicles, setVehicles]           = useState([]);
  const [drivers, setDrivers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterStatus, setFilterStatus]   = useState('all');
  const [addOpen, setAddOpen]             = useState(false);
  const [editOpen, setEditOpen]           = useState(false);
  const [viewOpen, setViewOpen]           = useState(false);
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [selected, setSelected]           = useState(null);
  const [form, setForm]                   = useState(emptyForm());
  const [saving, setSaving]               = useState(false);
  const [showThirdParty, setShowThirdParty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [incs, vehs, drvs] = await Promise.all([api.getIncidents(), api.getVehicles(), api.getDrivers()]);
      setIncidents(incs);
      setVehicles(vehs);
      setDrivers(drvs || []);
    } catch { toast.error('Failed to load incidents'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateForm = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const _wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;
  const _warnIfOverLimits = () => {
    if (_wordCount(form.location) > 30) {
      toast.warning('Location is over 30 words — please shorten it.');
      return false;
    }
    if (_wordCount(form.description) > 500) {
      toast.warning('Description is over 500 words — please shorten it.');
      return false;
    }
    if (_wordCount(form.damage_description) > 500) {
      toast.warning('Damage description is over 500 words — please shorten it.');
      return false;
    }
    return true;
  };

  const _extractApiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) return detail.map(d => d?.msg || String(d)).join(', ');
    if (typeof detail === 'string') return detail;
    return fallback;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.description) { toast.error('Vehicle and description are required'); return; }
    if (!form.date) { toast.error('Date is required'); return; }
    if (!form.damage_estimate || parseFloat(form.damage_estimate) <= 0) {
      toast.error('Estimated Repair Cost is required — enter the amount so it flows through to reports.');
      return;
    }
    if (!form.driver_id) { toast.error('Please select the driver responsible for this incident.'); return; }
    if (!_warnIfOverLimits()) return;
    setSaving(true);
    try {
      await api.createIncident({
        ...form,
        incident_date: form.date,
        damage_estimate: form.damage_estimate ? parseFloat(form.damage_estimate) : null,
      });
      toast.success('Incident recorded');
      setAddOpen(false);
      setForm(emptyForm());
      fetchData();
    } catch (err) { toast.error(_extractApiError(err, 'Failed to save')); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error('Date is required'); return; }
    if (!form.damage_estimate || parseFloat(form.damage_estimate) <= 0) {
      toast.error('Estimated Repair Cost is required — enter the amount so it flows through to reports.');
      return;
    }
    if (!form.driver_id) { toast.error('Please select the driver responsible for this incident.'); return; }
    if (!_warnIfOverLimits()) return;
    setSaving(true);
    try {
      await api.updateIncident(selected.id, {
        ...form,
        incident_date: form.date,
        damage_estimate: form.damage_estimate ? parseFloat(form.damage_estimate) : null,
      });
      toast.success('Incident updated');
      setEditOpen(false);
      fetchData();
    } catch (err) { toast.error(_extractApiError(err, 'Failed to update')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.deleteIncident(selected.id);
      toast.success('Incident deleted');
      setDeleteOpen(false);
      setSelected(null);
      setIncidents(prev => prev.filter(i => i.id !== selected.id));
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (inc) => {
    setSelected(inc);
    setForm({
      vehicle_id: inc.vehicle_id,
      date: inc.incident_date || inc.date || '',
      incident_type: inc.incident_type,
      description: inc.description,
      location: inc.location || '',
      driver_id: inc.driver_id || '',
      third_party_name: inc.third_party_name || '',
      third_party_contact: inc.third_party_contact || '',
      third_party_vehicle_reg: inc.third_party_vehicle_reg || '',
      police_case_number: inc.police_case_number || '',
      damage_description: inc.damage_description || '',
      damage_estimate: inc.damage_estimate || '',
      status: inc.status || 'open',
      resolution_notes: inc.resolution_notes || '',
    });
    setEditOpen(true);
  };

  const filtered = incidents.filter(i => {
    if (filterVehicle !== 'all' && i.vehicle_id !== filterVehicle) return false;
    if (filterStatus  !== 'all' && i.status     !== filterStatus)  return false;
    return true;
  });

  const openCount   = incidents.filter(i => i.status === 'open').length;
  const closedCount = incidents.filter(i => i.status === 'closed').length;

  // Driver leaderboard: incidents grouped by driver across last 12 months (in-memory from loaded incidents)
  const driverPhotoById = drivers.reduce((acc, d) => { if (d.photo_url) acc[d.id] = d.photo_url; return acc; }, {});
  const driverLeaderboard = (() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 12);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    const acc = {};
    for (const inc of incidents) {
      const dateStr = inc.incident_date || inc.date;
      if (!dateStr || dateStr < cutoffIso) continue;
      const key = inc.driver_id || 'unassigned';
      const name = inc.driver_name || 'Unassigned';
      if (!acc[key]) acc[key] = { driver_id: key, driver_name: name, photo_url: driverPhotoById[key] || null, total: 0, damage: 0, open: 0 };
      acc[key].total += 1;
      acc[key].damage += parseFloat(inc.damage_estimate || 0) || 0;
      if (inc.status === 'open') acc[key].open += 1;
    }
    return Object.values(acc).sort((a, b) => b.total - a.total || b.damage - a.damage).slice(0, 5);
  })();

  // IncidentForm has been extracted to ../components/IncidentForm.jsx to fix
  // the focus-loss bug (nested component was recreated on every keystroke).

  if (loading) return <div className="p-6 text-center text-gray-400">Loading incidents...</div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Incident Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Accidents, breakdowns, theft, vandalism and near misses</p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setShowThirdParty(false); setAddOpen(true); }} className="bg-gray-800 hover:bg-gray-700" data-testid="add-incident-btn">
          <Plus className="w-4 h-4 mr-2" />Report Incident
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: incidents.length, cls: 'text-gray-900', bg: 'bg-gray-50' },
          { label: 'Open',  value: openCount,        cls: 'text-red-600',   bg: 'bg-red-50' },
          { label: 'In Progress', value: incidents.filter(i => i.status === 'in_progress').length, cls: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Closed', value: closedCount,     cls: 'text-green-600', bg: 'bg-green-50' },
        ].map(c => (
          <Card key={c.label} className={`border-gray-200 ${c.bg}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{c.label}</p>
              <p className={`text-3xl font-heading font-bold ${c.cls}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Incidents by Driver (last 12 months) */}
      {driverLeaderboard.length > 0 && (
        <Card data-testid="driver-leaderboard-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-heading font-bold">Incidents by Driver</CardTitle>
                <p className="text-xs text-gray-500 font-mono uppercase tracking-wider mt-0.5">Last 12 months · Top 5</p>
              </div>
              <a href="/reports" className="text-xs font-mono uppercase tracking-wider text-blue-600 hover:text-blue-800">Full report →</a>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {driverLeaderboard.map((d, i) => (
                <div key={d.driver_id} className="border rounded-md p-3 bg-white hover:border-slate-900 transition-colors" data-testid={`driver-leaderboard-${d.driver_id}`}>
                  <div className="flex items-start gap-2.5 mb-1">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0" data-testid={`driver-leaderboard-avatar-${d.driver_id}`}>
                      {d.photo_url ? (
                        <img
                          src={d.photo_url.startsWith('http') ? d.photo_url : `${process.env.REACT_APP_BACKEND_URL}${d.photo_url}`}
                          alt={d.driver_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-[11px] font-heading font-bold text-slate-500">
                          {(d.driver_name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">#{i + 1}</span>
                        <span className={`text-2xl font-heading font-black leading-none ${d.open > 0 ? 'text-red-600' : 'text-slate-900'}`}>{d.total}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 truncate" title={d.driver_name}>{d.driver_name}</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    R {d.damage.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} damage
                    {d.open > 0 && <span className="ml-1.5 text-red-600">· {d.open} open</span>}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterVehicle} onValueChange={setFilterVehicle}>
          <SelectTrigger className="w-[200px]" data-testid="filter-vehicle"><SelectValue placeholder="All vehicles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        {(filterVehicle !== 'all' || filterStatus !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterVehicle('all'); setFilterStatus('all'); }}>
            <X className="w-4 h-4 mr-1" />Clear filters
          </Button>
        )}
      </div>

      {/* Incident List */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">{incidents.length === 0 ? 'No incidents recorded' : 'No incidents match filters'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map(inc => {
                const typeCfg = INCIDENT_TYPES.find(t => t.value === inc.incident_type) || INCIDENT_TYPES[5];
                const TypeIcon = typeCfg.icon;
                const stsCfg = STATUS_STYLES[inc.status] || STATUS_STYLES.open;
                return (
                  <div key={inc.id} className="flex items-start gap-4 p-5 hover:bg-gray-50 transition-colors" data-testid={`incident-${inc.id}`}>
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${TYPE_STYLES[inc.incident_type]}`}>
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{inc.vehicle_name}</span>
                        <Badge className={`text-xs ${TYPE_STYLES[inc.incident_type]}`}>{typeCfg.label}</Badge>
                        <Badge className={`text-xs ${stsCfg.bg} ${stsCfg.text}`}>{stsCfg.label}</Badge>
                        {inc.damage_estimate && (
                          <span className="text-xs text-gray-500 font-data">R{inc.damage_estimate.toFixed(2)}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{inc.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>{inc.incident_date || inc.date}</span>
                        {inc.location && <span>• {inc.location}</span>}
                        {inc.driver_name && <span>• {inc.driver_name}</span>}
                        {inc.police_case_number && <span>• Case: {inc.police_case_number}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => { setSelected(inc); setViewOpen(true); }} data-testid={`view-incident-${inc.id}`} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(inc)} data-testid={`edit-incident-${inc.id}`} title="Edit"><Edit className="w-4 h-4 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setSelected(inc); setDeleteOpen(true); }} data-testid={`delete-incident-${inc.id}`} title="Delete"><Trash2 className="w-4 h-4 text-gray-400" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Report Incident</DialogTitle>
            <DialogDescription>Record an accident, breakdown, theft, or other vehicle incident</DialogDescription>
          </DialogHeader>
          <IncidentForm
            form={form}
            updateForm={updateForm}
            vehicles={vehicles}
            drivers={drivers}
            incidentTypes={INCIDENT_TYPES}
            showThirdParty={showThirdParty}
            setShowThirdParty={setShowThirdParty}
            saving={saving}
            onSubmit={handleAdd}
            onCancel={() => setAddOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Incident</DialogTitle>
            <DialogDescription>Edit incident details, status, or resolution notes</DialogDescription>
          </DialogHeader>
          <IncidentForm
            form={form}
            updateForm={updateForm}
            vehicles={vehicles}
            drivers={drivers}
            incidentTypes={INCIDENT_TYPES}
            showThirdParty={showThirdParty}
            setShowThirdParty={setShowThirdParty}
            saving={saving}
            onSubmit={handleEdit}
            onCancel={() => setEditOpen(false)}
            isEdit
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      {selected && viewOpen && (
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {selected.vehicle_name} — {INCIDENT_TYPES.find(t => t.value === selected.incident_type)?.label}
              </DialogTitle>
              <DialogDescription>{selected.date}{selected.location ? ` • ${selected.location}` : ''}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_STYLES[selected.status]?.bg} ${STATUS_STYLES[selected.status]?.text}`}>
                  {STATUS_STYLES[selected.status]?.label}
                </Badge>
                {selected.damage_estimate && (
                  <span className="font-data text-gray-700">Estimated cost: R{selected.damage_estimate.toFixed(2)}</span>
                )}
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-xs font-semibold text-gray-500 mb-1">Description</p>
                <p className="text-gray-800">{selected.description}</p>
              </div>
              {selected.damage_description && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Damage</p>
                  <p className="text-gray-800">{selected.damage_description}</p>
                </div>
              )}
              {selected.driver_name && <p><span className="font-medium">Driver:</span> {selected.driver_name}</p>}
              {selected.police_case_number && <p><span className="font-medium">Police Case:</span> <span className="font-data">{selected.police_case_number}</span></p>}
              {(selected.third_party_name || selected.third_party_vehicle_reg) && (
                <div className="border rounded-md p-3 space-y-1">
                  <p className="text-xs font-semibold text-gray-500">Third Party</p>
                  {selected.third_party_name && <p>{selected.third_party_name}</p>}
                  {selected.third_party_contact && <p className="font-data">{selected.third_party_contact}</p>}
                  {selected.third_party_vehicle_reg && <p className="font-data">{selected.third_party_vehicle_reg}</p>}
                </div>
              )}
              {selected.resolution_notes && (
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-xs font-semibold text-green-700 mb-1">Resolution</p>
                  <p className="text-gray-800">{selected.resolution_notes}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Incident?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this incident report. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
